import { ReactElement } from "react";
import {
	DateString,
	IconAndText,
	ListItem,
	Space,
	PipeSeparator
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { HubThumbnail } from "client/Features/Hub/HubThumbnail/HubThumbnail";
import { HubItemMediaPreviewViewModel, ReactionCounterModel } from "client/Api/Hub/common";
import { HubItemCategoryModel } from "client/Api/Hub/hub";
import { PreviewItemUserModel } from "client/Api/Hub/Events/eventViewer";
import styles from "./HubListItem.module.less";
import { NumberFormatter } from "client/Hooks";
import { useHubStatisticsFormatter } from "client/Features/Hub/useHubStatisticsFormatter";
import { TimeZone } from "client/DateAndTime/timezones";
import { generateTrackingId, isNotNil } from "client/Misc/utils";
import { ReactionStatistic, useReactionStatistic } from "client/Features/Reactions/useReactionStatistic";

export type HubListItemProps = {
	title: string;
	category: HubItemCategoryModel | undefined;
	author: PreviewItemUserModel | null;
	location: string | null;
	startDate: Date;
	attendeeCount: number | null;
	media: HubItemMediaPreviewViewModel;
	onNavigate(): void;
	trackingId: string;
	timezone: TimeZone;
	isNew: boolean;
	numberOfComments: number;
	reactionCounters: readonly ReactionCounterModel[];
	areCommentsEnabled: boolean;
};

export const HubListItem = (props: HubListItemProps): ReactElement => {
	const {
		title,
		category,
		author,
		location,
		startDate,
		attendeeCount,
		media,
		onNavigate,
		trackingId,
		timezone,
		isNew,
		numberOfComments,
		reactionCounters,
		areCommentsEnabled
	} = props;

	const statisticsFormatter: NumberFormatter = useHubStatisticsFormatter();
	const reactionStatistic: ReactionStatistic = useReactionStatistic(reactionCounters);

	return (
		<ListItem
			trackingId={trackingId}
			hasStatusDot={isNew}
		>
			<ListItem.Content
				onClick={onNavigate}
				thumbnail={(
					<div className={styles.thumbnail}>
						<HubThumbnail
							title={title}
							media={media}
							hubItemKind={HubItemKind.Event}
							mediaPlaceholderSize="small"
						/>
					</div>
				)}
			>
				<ListItem.Title>
					{title}
				</ListItem.Title>
				<ListItem.SubTitle>
					<DateString
						value={startDate}
						format="DMYT"
						timezone={timezone}
					/>
					{author?.name && (
						<>
							<PipeSeparator />
							{localize("_(Edited)By")}
							<Space />
							{author.name}
						</>
					)}
					{category && (
						<>
							<PipeSeparator />
							{category.name}
						</>
					)}
				</ListItem.SubTitle>
				<div className={styles.stats}>
					{isNotNil(attendeeCount) && (
						<IconAndText
							text={statisticsFormatter(attendeeCount)}
							trackingId="attendees"
						>
							<IconAndText.Icon
								value="user-group"
								size="small"
							/>
						</IconAndText>
					)}
					{location && (
						<div className={styles.location}>
							<IconAndText
								text={location}
								trackingId="location"
							>
								<IconAndText.Icon
									value="location-dot"
									size="small"
								/>
							</IconAndText>
						</div>
					)}
					<IconAndText
						text={statisticsFormatter(reactionStatistic.total)}
						trackingId="reaction-count"
					>
						<IconAndText.Icon
							value={reactionStatistic.icon}
							size="small"
							trackingId={generateTrackingId("top-reaction", `|${reactionStatistic.kind ?? "none"}`)}
						/>
					</IconAndText>
					{areCommentsEnabled && (
						<IconAndText
							text={statisticsFormatter(numberOfComments)}
							trackingId="comment-count"
						>
							<IconAndText.Icon
								value="comment"
								size="small"
							/>
						</IconAndText>
					)}
				</div>
			</ListItem.Content>
		</ListItem>
	);
};

HubListItem.displayName = "HubListItem";